import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
// Context
import { useFiltersContext } from 'components/common3/PageWithFiltersWrapper/PageWithFiltersContext';
// Api
import { GET_ARTICLES } from 'api/articles/queries';
// Types
import type {
  GetArticleForStore,
  GetArticleForStore_getArticleForStore_entities,
  GetArticleForStoreVariables,
} from 'api/articles/types/GetArticleForStore';
import {
  ArticleStatus,
  ArticleStoreFilterInput,
} from 'api/graphql-global-types';
import { SearchLocation } from 'api/graphql-analytics-global-types';
// Ui
import FourDotsLoader from 'ui/FourDotsLoader/FourDotsLoader';
import InfiniteScrollLoader from 'ui/InfiniteScrollLoader/InfiniteScrollLoader';
// Components
import InfiniteScroll from 'components/common3/InfiniteScroll/InfiniteScroll';
import NotFound from 'components/common/NotFound/NotFound';
import ContentCard, { ContentType } from 'ui3/Card/ContentCard';
import PageWithFiltersWrapper from 'components/common3/PageWithFiltersWrapper/PageWithFiltersWrapper';
// Constants
import { ATHLETES } from 'constants/routes';
// Styles
import styles from './AllNews.module.scss';

const PER_PAGE = 9;

type AllNewsProps = {
  storeId: string;
};

export const AllNews = ({ storeId }: AllNewsProps): JSX.Element | null => {
  const { sorting, searchKey, search } = useFiltersContext();

  const inputVariables = useMemo(
    (): ArticleStoreFilterInput => ({
      storeId,
      statuses: [ArticleStatus.Active],
      orderBy: sorting.order,
      direction: sorting.direction,
      limit: PER_PAGE,
      offset: 0,
      ...(searchKey &&
        search[searchKey] && {
          [searchKey]: search[searchKey],
        }),
    }),
    [storeId, sorting, searchKey, search]
  );

  const { data, loading, fetchMore } = useQuery<
    GetArticleForStore,
    GetArticleForStoreVariables
  >(GET_ARTICLES, {
    variables: {
      input: inputVariables,
    },
    fetchPolicy: 'cache-and-network',
    skip: !storeId,
  });

  const loadedArticles = data?.getArticleForStore?.entities || [];
  // A store-less article (a topic page) has no /{store}/news route to link to.
  const sponsoredArticles = loadedArticles.filter(
    (article) => article.store?.slug
  );
  const total = data?.getArticleForStore?.total;
  // Paging counts what the server returned, so a hidden article cannot stall it.
  const hasMore = data ? loadedArticles.length < (total ?? 0) : false;
  const isNotFound = !loading && !total;

  const fetchMoreData = () => {
    const prevOffset = data?.getArticleForStore?.offset || 0;
    const offset = prevOffset ? prevOffset + PER_PAGE : PER_PAGE;

    fetchMore({
      variables: {
        input: {
          ...inputVariables,
          offset,
        },
      },
    });
  };

  if (!storeId) {
    return null;
  }

  return (
    <>
      {loading && <FourDotsLoader />}
      <PageWithFiltersWrapper
        sortKey="News"
        skipFilters
        title="News"
        loading={loading}
        filters={null}
        singleCategory
        isPage={false}
        searchBarLocation={SearchLocation.Articles}
      >
        <InfiniteScroll
          dataLength={sponsoredArticles.length}
          next={fetchMoreData}
          hasMore={hasMore}
          loader={<InfiniteScrollLoader />}
          isOverflowVisible
        >
          {isNotFound && <NotFound title="News not found" />}
          {!isNotFound && (
            <ul className={styles.newsWrapper}>
              {sponsoredArticles.map(
                (article: GetArticleForStore_getArticleForStore_entities) => {
                  return (
                    <li key={article.id}>
                      <ContentCard
                        descriptionClassName={styles.infoWrapper}
                        type={ContentType.NEWS}
                        imageUrl={article.url || ''}
                        tzCode={null}
                        linkUrl={`/${article.store?.slug}/news/${article.slug}`}
                        dateTime={article.createdAt || ''}
                        title={article.title || ''}
                        user={article.store?.storeDetails?.storeName || ''}
                        userUrl={
                          article.store?.slug
                            ? `/${article.store?.slug}`
                            : ATHLETES
                        }
                        isPlay={false}
                        isArticleCard
                      />
                    </li>
                  );
                }
              )}
            </ul>
          )}
        </InfiniteScroll>
      </PageWithFiltersWrapper>
    </>
  );
};

export default AllNews;
